/*
 * Pure helpers of the QM-Cockpit (workflow-manager `quality-cases`): texts, tones, sorting, inbox merge with the
 * early warning records, outcome step labels and KPI formatting. UI copy German.
 */
import moment from 'moment';
import {StateCodes, StateCodesVue} from '@/enums/StateCodes.enum';
import {
    QualityCaseGroupTrigger,
    QualityCaseOutcome,
    QualityCasePriority,
    QualityCaseStatus,
    QualityCaseStep,
    QualityCaseType,
    QualityCheckInTarget,
    QualityOnboardingTouchpointKind,
    QUALITY_MULTI_CONTACT_TYPES,
    QualityCaseContactStatus,
    QualityComplaintCategory,
    QualityComplaintSeverity,
    QualityOnboardingTarget,
} from '@/enums/workflow-manager/QualityCase.enum';
import {SimpleRating} from '@/enums/_common/SimpleRating';
import {QualityCaseLean} from '@/classes/dto/workflow-manager/quality-cases/QualityCase.response.dto';
import {QualityCaseExecutedStep} from '@/documentTypes/workflow-manager/QualityCase.document';
import {AbsenceEarlyWarningRecordItem} from '@/classes/api/absence-early-warning.api';
import {Tone} from '@/components/earlyWarning/earlyWarning.helper';
import {LessonFormat} from '@/enums/catalogue-manager/LessonFormat.enum';

export type QmTab = 'inbox' | 'rescue' | 'groups' | 'onboarding' | 'feedback' | 'risk' | 'people' | 'earlyWarning' | 'impact' | 'settings';

export const QM_TABS: QmTab[] = ['inbox', 'rescue', 'groups', 'onboarding', 'feedback', 'risk', 'people', 'earlyWarning', 'impact', 'settings'];

/** Case types of the tab "Risiko & Übergang" (Paket 2) */
export const RISK_TYPES: QualityCaseType[] = [QualityCaseType.RISK, QualityCaseType.TRANSITION];

/** Old tab keys of links (e.g. "ew") */
export const tabFromQuery = (value: unknown): QmTab | null => {
    const tab = value === 'ew' ? 'earlyWarning' : String(value || '');
    return QM_TABS.includes(tab as QmTab) ? tab as QmTab : null;
};

/** Case types of the tab "Lehrkräfte & Standorte" */
export const PEOPLE_TYPES: QualityCaseType[] = [QualityCaseType.TEACHER, QualityCaseType.INSTITUTION, QualityCaseType.CHECK_IN];

const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/**
 * z-index of the QM menus (Zuweisen, Anrufen): above cards / sticky rows of the cockpit and above the Akte dialog
 * (Vuetify dialogs from 202), so the menu is never covered and clicks never reach the card behind it
 */
export const QM_MENU_Z_INDEX = 300;

export const fmtDate = (value?: Date | string | null): string => value ? moment(value).format('DD.MM.YYYY') : '–';
export const fmtShort = (value?: Date | string | null): string => value ? moment(value).format('DD.MM.') : '–';

export const minutesToTime = (minutes?: number): string => typeof minutes === 'number'
    ? `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}` : '';

export const weekdayShort = (day?: number): string => typeof day === 'number' ? WEEKDAY_SHORT[day] || '' : '';

export const stateShort = (state?: StateCodes | string): string => state ? String(state).toUpperCase() : '';

/** "Gitarre Di 14:00" */
export const caseCourseText = (c: Pick<QualityCaseLean, 'display'>): string => {
    const d = c.display || {};
    return [d.subjectName, [weekdayShort(d.dayOfWeek), minutesToTime(d.startTime)].filter(Boolean).join(' ')].filter(Boolean).join(' ');
};

/** All courses of a case: own course + every course of the termination (a termination may cover several, e.g. KS010 + KS007) */
export const caseCourseIds = (c: Pick<QualityCaseLean, 'courseCustomId' | 'terminationSnapshot'>): string[] =>
    Array.from(new Set([
        c.courseCustomId,
        ...(c.terminationSnapshot?.courseCustomIds || []),
        ...((c.terminationSnapshot?.participations || []) as Array<{ courseCustomId?: string }>).map((p) => p.courseCustomId),
    ].filter((id): id is string => !!id)));

/**
 * Rescue case whose termination covers several courses: switch / online / pause are refused by the server (edit the
 * courses one by one in the course plan) – the reason shown at the options and at "Übernehmen" (same text as the server)
 */
export const multiCourseBlock = (c: Pick<QualityCaseLean, 'type' | 'courseCustomId' | 'terminationSnapshot'>): string => {
    const ids = caseCourseIds(c);
    return c.type === QualityCaseType.RESCUE && ids.length > 1
        ? `Die Kündigung betrifft mehrere Kurse (${ids.join(', ')}). Wechsel oder Pause bitte im Kursplan einzeln bearbeiten; „Bleibt“ ist möglich.`
        : '';
};

/** Outcomes that need exactly one terminated course */
export const SINGLE_COURSE_OUTCOMES: QualityCaseOutcome[] = [QualityCaseOutcome.OTHER_COURSE, QualityCaseOutcome.ONLINE, QualityCaseOutcome.PAUSED];

/** "GS Wolfartsweier, Karlsruhe" */
export const casePlaceText = (c: Pick<QualityCaseLean, 'display'>): string =>
    [c.display?.institutionName, c.display?.city].filter(Boolean).join(', ');

export const TYPE_META: Record<QualityCaseType, { text: string; icon: string; tone: Tone }> = {
    [QualityCaseType.RESCUE]: {text: 'Rückgewinnung', icon: 'mdi-alert-outline', tone: 'bad'},
    [QualityCaseType.GROUP]: {text: 'Gruppe', icon: 'mdi-account-group-outline', tone: 'warn'},
    [QualityCaseType.ONBOARDING]: {text: 'Kontaktpunkt', icon: 'mdi-sprout-outline', tone: 'info'},
    [QualityCaseType.TEACHER]: {text: 'Lehrkraft', icon: 'mdi-human-male-board', tone: 'bad'},
    [QualityCaseType.INSTITUTION]: {text: 'Standort', icon: 'mdi-office-building-outline', tone: 'bad'},
    [QualityCaseType.CHECK_IN]: {text: 'Regelgespräch', icon: 'mdi-calendar-sync-outline', tone: 'muted'},
    [QualityCaseType.RISK]: {text: 'Risiko', icon: 'mdi-chart-bell-curve-cumulative', tone: 'warn'},
    [QualityCaseType.COMPLAINT]: {text: 'Beschwerde', icon: 'mdi-message-alert-outline', tone: 'bad'},
    [QualityCaseType.TRANSITION]: {text: 'Übergang Klasse 4/6', icon: 'mdi-school-outline', tone: 'info'},
};

export const PRIORITY_META: Record<QualityCasePriority, { text: string; tone: Tone; rank: number }> = {
    [QualityCasePriority.HIGH]: {text: 'Hoch', tone: 'bad', rank: 0},
    [QualityCasePriority.NORMAL]: {text: 'Normal', tone: 'info', rank: 1},
    [QualityCasePriority.LOW]: {text: 'Niedrig', tone: 'muted', rank: 2},
};

export const OUTCOME_TEXT: Record<string, string> = {
    [QualityCaseOutcome.STAYS]: 'Bleibt',
    [QualityCaseOutcome.OTHER_COURSE]: 'Anderer Kurs',
    [QualityCaseOutcome.ONLINE]: 'Online weiter',
    [QualityCaseOutcome.PAUSED]: 'Pausiert',
    [QualityCaseOutcome.LEAVES]: 'Geht',
    [QualityCaseOutcome.NOT_REACHED]: 'Nicht erreicht',
    [QualityCaseOutcome.OK]: 'Alles gut',
    [QualityCaseOutcome.PROBLEM]: 'Problem',
    [QualityCaseOutcome.TALK_DONE]: 'Gespräch geführt',
    [QualityCaseOutcome.RESOLVED]: 'Erledigt sich',
    [QualityCaseOutcome.MERGED]: 'Zusammengeführt',
    [QualityCaseOutcome.SOLVED]: 'Gelöst',
    [QualityCaseOutcome.UNSOLVED]: 'Nicht gelöst',
    [QualityCaseOutcome.CONTINUES_ONLINE]: 'Weiter online',
    [QualityCaseOutcome.OTHER_INSTITUTION]: 'Anderer Standort',
    [QualityCaseOutcome.CONTINUES_HERE]: 'Bleibt am Standort',
};

/** Transition "Geht": there is no termination – the child stops after the summer holidays (stored value unchanged) */
export const TRANSITION_LEAVES_TEXT = 'Hört auf – nach den Sommerferien';

/** Outcome label of a case type (transition LEAVES has its own wording) */
export const outcomeText = (type: QualityCaseType | string | undefined, outcome: string): string =>
    (type === QualityCaseType.TRANSITION && outcome === QualityCaseOutcome.LEAVES ? TRANSITION_LEAVES_TEXT : OUTCOME_TEXT[outcome] || outcome);

/** Type label of one case: a transition case names its stored grade ("Übergang Klasse 6" in Berlin / Brandenburg) */
export const caseTypeText = (c: Pick<QualityCaseLean, 'type' | 'transition'>): string =>
    (c.type === QualityCaseType.TRANSITION ? `Übergang Klasse ${c.transition?.grade ?? 4}` : TYPE_META[c.type]?.text || c.type);

/** Risk reason noSubscription / group child without a matching subscription (same text as the backend) */
export const NO_SUBSCRIPTION_TITLE = 'Das Kind ist im Kurs eingetragen, aber im Rechnungslauf läuft kein Unterrichts-Abo mit Kurszulassung für dieses Fach – '
    + 'Abo beendet (stille Kündigung?) oder nicht angelegt (Abrechnung prüfen).';

/** Transition grades in words: "Klasse 4 (Berlin, Brandenburg: Klasse 6)" – settings.transition, defaults BE / BB 6 */
export const transitionGradeText = (cfg?: { grade?: number; gradeByState?: Partial<Record<string, number>> } | null): string => {
    const grade = cfg?.grade || 4;
    const byState = cfg?.gradeByState || {BE: 6, BB: 6};
    const groups = new Map<number, string[]>();
    Object.entries(byState).filter(([, g]) => typeof g === 'number' && g !== grade).forEach(([state, g]) => {
        groups.set(g as number, [...(groups.get(g as number) || []), StateCodesVue.find((s) => s.value === state)?.text || state]);
    });
    const extra = Array.from(groups.entries()).map(([g, states]) => `${states.join(', ')}: Klasse ${g}`).join('; ');
    return `Klasse ${grade}${extra ? ` (${extra})` : ''}`;
};

/** tel: href without spaces / formatting ("+49 (0)151 / 871-92 28" → "tel:+491518719228"); the display text stays */
export const telHref = (phone?: string | null): string => {
    const digits = (phone || '').replace(/\(0\)/g, '').replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
    return digits ? `tel:${digits}` : '';
};

export const outcomeTone = (outcome?: string): Tone => {
    if (!outcome) {
        return 'muted';
    }
    if (QualityCaseOutcome.RESCUED.includes(outcome as QualityCaseOutcome) || outcome === QualityCaseOutcome.OK || outcome === QualityCaseOutcome.TALK_DONE
        || outcome === QualityCaseOutcome.RESOLVED || outcome === QualityCaseOutcome.MERGED || outcome === QualityCaseOutcome.SOLVED
        || QualityCaseOutcome.TRANSITIONED.includes(outcome as QualityCaseOutcome)) {
        return 'good';
    }
    if (outcome === QualityCaseOutcome.LEAVES || outcome === QualityCaseOutcome.PROBLEM || outcome === QualityCaseOutcome.UNSOLVED) {
        return 'bad';
    }
    return 'warn';
};

export const STEP_LABEL: Record<QualityCaseStep, string> = {
    [QualityCaseStep.UNDO_TERMINATION]: 'Kündigung zurücknehmen',
    [QualityCaseStep.REMOVE_FROM_COURSE]: 'Aus dem bisherigen Kurs austragen',
    [QualityCaseStep.ADD_TO_COURSE]: 'In den neuen Kurs eintragen',
    [QualityCaseStep.PAUSE_BILLING]: 'Abrechnung pausieren',
    [QualityCaseStep.CREATE_RETURN_FOLLOW_UP]: 'Fall „Rückkehr nach Pause“ anlegen',
    [QualityCaseStep.UPDATE_RECURRING_SNAPSHOT]: 'Rentabilität: Kurs an der Abo-Position aktualisieren',
    [QualityCaseStep.CREDIT_BILLED_PAUSE]: 'Gutschrift für bereits berechnete Termine',
};

export const stepLabel = (step: QualityCaseExecutedStep): string => STEP_LABEL[step.step] || step.step;

export const CLOSED_REASON_TEXT: Record<string, string> = {
    dueDatePassed: 'Kündigungsdatum verstrichen ohne Ergebnis',
    studentLeft: 'Schüler:in nicht mehr im Kurs',
    maxAttempts: 'Nach maximalen Anrufversuchen nicht erreicht',
    discarded: 'verworfen',
    parentDone: 'mit dem übergeordneten Fall erledigt',
    merged: 'mit einem offenen Fall zusammengeführt',
    ownEmployee: 'betrifft die zuständige QM-Person selbst',
    courseIndividual: 'Einzelunterricht (keine Gruppe)',
    graceDaysPassed: 'Frist verstrichen',
    summerStarted: 'Sommerferien haben begonnen',
    alertResolved: 'Auffälligkeit besteht nicht mehr',
};

/** Target group of an onboarding case (cases without onboardingTarget are child cases) */
export const onboardingTargetOf = (c: Pick<QualityCaseLean, 'onboardingTarget'>): QualityOnboardingTarget =>
    (c.onboardingTarget as QualityOnboardingTarget) || QualityOnboardingTarget.CHILD;

/** "Kontaktpunkt · Lehrkraft" / "… · Standort" ('' for other cases and child onboarding) */
export const onboardingTypeText = (c: Pick<QualityCaseLean, 'type' | 'onboardingTarget'>): string => {
    const target = c.type === QualityCaseType.ONBOARDING ? onboardingTargetOf(c) : QualityOnboardingTarget.CHILD;
    return target === QualityOnboardingTarget.CHILD ? '' : `Kontaktpunkt · ${QualityOnboardingTarget.Record[target]?.text || ''}`;
};

/** Who/what the case is about (title line) */
export const caseSubject = (c: QualityCaseLean): string => {
    const d = c.display || {};
    if (c.type === QualityCaseType.ONBOARDING && onboardingTargetOf(c) === QualityOnboardingTarget.TEACHER) {
        return d.teacherName || 'Lehrkraft';
    }
    if (c.type === QualityCaseType.ONBOARDING && onboardingTargetOf(c) === QualityOnboardingTarget.INSTITUTION) {
        return casePlaceText(c) || 'Standort';
    }
    switch (c.type) {
        case QualityCaseType.RESCUE:
        case QualityCaseType.ONBOARDING:
        case QualityCaseType.RISK:
        case QualityCaseType.TRANSITION:
            return d.studentName || 'Schüler:in';
        case QualityCaseType.COMPLAINT:
            return d.studentName || d.teacherName || casePlaceText(c) || 'ohne Bezug';
        case QualityCaseType.GROUP:
            return [c.courseCustomId, caseCourseText(c)].filter(Boolean).join(' ');
        case QualityCaseType.TEACHER:
            return d.teacherName || 'Lehrkraft';
        case QualityCaseType.INSTITUTION:
            return casePlaceText(c) || 'Standort';
        case QualityCaseType.CHECK_IN:
            return c.checkInTarget === QualityCheckInTarget.TEACHER ? (d.teacherName || 'Lehrkraft') : (casePlaceText(c) || 'Standort');
        default:
            return '';
    }
};

/** "Rückgewinnung · Mia K." / "Gruppe schrumpft · KS1182 Gitarre Mi 13:30" */
export const caseTitle = (c: QualityCaseLean): string => {
    switch (c.type) {
        case QualityCaseType.GROUP:
            if (c.display?.lessonFormat === LessonFormat.DUO && c.participantsNow === 1) {
                return `Partner fehlt (zu zweit) · ${caseSubject(c)}`;
            }
            return `${c.trigger === QualityCaseGroupTrigger.BELOW_MIN ? 'Gruppe unter Mindestgröße' : 'Gruppe schrumpft'} · ${caseSubject(c)}`;
        case QualityCaseType.ONBOARDING: {
            const target = onboardingTargetOf(c);
            const group = target === QualityOnboardingTarget.CHILD ? '' : `${QualityOnboardingTarget.Record[target]?.text} · `;
            const label = c.trigger === QualityCaseGroupTrigger.FEEDBACK ? `Feedback${c.touchpointLabel ? ` (${c.touchpointLabel})` : ''}` : c.touchpointLabel || 'Kontakt';
            return `${c.recurring ? 'Regelmäßiger Kontakt' : 'Kontaktpunkt'} · ${group}${label} · ${caseSubject(c)}`;
        }
        case QualityCaseType.TEACHER:
        case QualityCaseType.INSTITUTION: {
            // the real reason: a case only from the Scorecard / Standort-Gesundheit never reads like a termination spike
            const sources = alertSources(c);
            const head = c.type === QualityCaseType.TEACHER
                ? (sources.scorecard && !sources.numbers ? 'Scorecard auffällig' : 'Lehrkraft auffällig')
                : (sources.health && !sources.numbers ? 'Standort-Gesundheit kritisch' : 'Standort auffällig');
            return `${head} · ${caseSubject(c)}`;
        }
        case QualityCaseType.CHECK_IN:
            return `Regelgespräch · ${caseSubject(c)}`;
        case QualityCaseType.RISK:
            return `Risiko ${c.risk?.score ?? ''}/100 · ${caseSubject(c)}`;
        case QualityCaseType.COMPLAINT:
            return `Beschwerde · ${c.complaint ? QualityComplaintCategory.Record[c.complaint.category]?.text || '' : ''} · ${caseSubject(c)}`;
        case QualityCaseType.TRANSITION:
            return `${caseTypeText(c)} · ${caseSubject(c)}`;
        default:
            return `${TYPE_META[c.type]?.text || c.type} · ${caseSubject(c)}`;
    }
};

/** Meta line below the title */
export const caseMeta = (c: QualityCaseLean): string => {
    const parts: string[] = [];
    const course = caseCourseText(c);
    if (c.type === QualityCaseType.COMPLAINT) {
        if (c.complaint) {
            parts.push(QualityComplaintSeverity.Record[c.complaint.severity]?.text || '', c.complaint.reportedBy ? `gemeldet von ${c.complaint.reportedBy}` : '');
        }
        parts.push(course, casePlaceText(c));
        if (c.student && c.display?.teacherName) {
            parts.push(`Lehrkraft ${c.display.teacherName}`);
        }
    } else if (c.type === QualityCaseType.RISK || c.type === QualityCaseType.TRANSITION) {
        parts.push(course, casePlaceText(c));
        if (c.display?.teacherName) {
            parts.push(`Lehrkraft ${c.display.teacherName}`);
        }
        if (c.type === QualityCaseType.TRANSITION && c.transition?.summerStart) {
            parts.push(`Sommerferien ab ${fmtShort(c.transition.summerStart)}`);
        }
    } else if (c.type === QualityCaseType.RESCUE || c.type === QualityCaseType.ONBOARDING) {
        const target = c.type === QualityCaseType.ONBOARDING ? onboardingTargetOf(c) : QualityOnboardingTarget.CHILD;
        parts.push(course, target === QualityOnboardingTarget.INSTITUTION ? '' : casePlaceText(c));
        if (c.display?.teacherName && target !== QualityOnboardingTarget.TEACHER) {
            parts.push(`Lehrkraft ${c.display.teacherName}`);
        }
        if (c.type === QualityCaseType.RESCUE) {
            const courses = caseCourseIds(c);
            if (courses.length > 1) {
                parts.push(`Kurse ${courses.join(' + ')}`);
            }
            if (c.terminationSnapshot?.createdAt) {
                parts.push(`gekündigt am ${fmtShort(c.terminationSnapshot.createdAt)}`);
            }
            if (c.terminationSnapshot?.reason?.name) {
                parts.push(`Grund „${c.terminationSnapshot.reason.name}“`);
            }
        }
        if (c.type === QualityCaseType.ONBOARDING && c.prefill?.participationStart) {
            parts.push(`Start ${fmtShort(c.prefill.participationStart)}`);
        }
    } else if (c.type === QualityCaseType.GROUP) {
        parts.push(casePlaceText(c));
        if (c.participantsBefore !== undefined && c.participantsNow !== undefined) {
            parts.push(`${c.participantsBefore} → ${c.participantsNow} Kinder`);
        } else if (c.participantsNow !== undefined) {
            parts.push(`${c.participantsNow} Kinder aktiv`);
        }
        if (c.display?.teacherName) {
            parts.push(`Lehrkraft ${c.display.teacherName}`);
        }
    } else {
        parts.push(alertText(c));
    }
    return parts.filter(Boolean).join(' · ');
};

/** Short Scorecard flag names (as the workflow-manager texts) – older cases store only the flags, no signals */
const SCORECARD_FLAG_SHORT: Record<string, string> = {
    terminationRate: 'Kündigungsquote hoch',
    cancellationRate: 'viele Ausfälle',
    missingLogs: 'Protokolle fehlen',
    talkTrend: 'Gesprächsbewertung gesunken',
};
const TEACHER_SPIKE_REASONS = ['minCount', 'rate'];
const INSTITUTION_PROBLEM_REASONS = ['terminations', 'openGroupCases', 'cancellations'];

/**
 * Which sources raised a teacher / Standort alert: termination spike / Standort problems (numbers), Lehrkraft-Scorecard,
 * Standort-Gesundheit. Cases without reasons (older ones) count as numbers.
 */
export const alertSources = (c: Pick<QualityCaseLean, 'type' | 'alert'>): { numbers: boolean; scorecard: boolean; health: boolean } => {
    const reasons = c.alert?.reasons || [];
    const scorecard = c.type === QualityCaseType.TEACHER && reasons.includes('scorecard');
    const health = c.type === QualityCaseType.INSTITUTION && reasons.includes('health');
    const numberReasons = c.type === QualityCaseType.TEACHER ? TEACHER_SPIKE_REASONS : INSTITUTION_PROBLEM_REASONS;
    return {numbers: reasons.some((r) => numberReasons.includes(r)) || (!scorecard && !health), scorecard, health};
};

/** "Protokolle fehlen (70 %)" per Scorecard flag (stored signals, else the flag names of older cases) */
export const scorecardTexts = (c: Pick<QualityCaseLean, 'alert'>): string[] => {
    const stored = (c.alert?.signals || []).filter((s) => s.key in SCORECARD_FLAG_SHORT).map((s) => s.text);
    return stored.length ? stored : (c.alert?.reasons || []).filter((r) => r in SCORECARD_FLAG_SHORT).map((r) => SCORECARD_FLAG_SHORT[r]);
};

/** Numbers that triggered a teacher / Standort alert (only when they did), the Scorecard / health reason, or the last talk of a check-in */
export const alertText = (c: QualityCaseLean): string => {
    const a = c.alert || {};
    const sources = alertSources(c);
    if (c.type === QualityCaseType.TEACHER) {
        const scorecard = scorecardTexts(c);
        return [
            sources.numbers && a.terminations !== undefined ? `${a.terminations} Kündigungen in ${a.windowDays ?? '?'} Tagen` : '',
            sources.numbers && a.ratePercent !== undefined ? `${String(a.ratePercent).replace('.', ',')} % der Schüler:innen` : '',
            sources.scorecard ? `${sources.numbers ? 'Scorecard: ' : ''}${scorecard.join(', ') || 'auffällig'}` : '',
        ].filter(Boolean).join(' · ');
    }
    if (c.type === QualityCaseType.INSTITUTION) {
        const health = (a.signals || []).map((s) => s.text);
        return [
            sources.numbers && a.terminations !== undefined ? `${a.terminations} Kündigungen in ${a.windowDays ?? '?'} Tagen` : '',
            sources.numbers && a.openGroupCases !== undefined ? `${a.openGroupCases} offene Gruppenfälle` : '',
            sources.numbers && a.cancellations !== undefined ? `${a.cancellations} ausgefallene Stunden` : '',
            sources.health ? `${sources.numbers ? 'Standort-Gesundheit kritisch' : 'Gesundheit'}${typeof a.healthScore === 'number' ? ` ${a.healthScore}/100` : ''}` : '',
            ...(sources.health ? health : []),
        ].filter(Boolean).join(' · ');
    }
    if (c.type === QualityCaseType.CHECK_IN) {
        if (c.trigger === QualityCaseGroupTrigger.FOLLOW_UP) {
            return `Wiedervorlage aus einem Gespräch${c.dueDate ? ` zum ${fmtShort(c.dueDate)}` : ''}`;
        }
        return a.lastTalkAt ? `letztes Gespräch am ${fmtDate(a.lastTalkAt)}` : 'bisher kein Gespräch erfasst';
    }
    return '';
};

/** Open case without Zuständigkeit (no QM person for the Bundesland, all QM persons involved, no Bundesland) */
export const isUnassigned = (c: Pick<QualityCaseLean, 'status' | 'responsible'>): boolean => c.status === QualityCaseStatus.OPEN && !c.responsible;

/** Why the case has no Zuständigkeit ("kein Bundesland bekannt", "Marcel K ist die Lehrkraft, keine Vertretung hinterlegt") */
export const unassignedReason = (c: Pick<QualityCaseLean, 'responsibleNote' | 'state'>): string => {
    const note = (c.responsibleNote || '').replace(/\s*–\s*(ohne Zuständigkeit, )?bitte manuell zuteilen\s*$/, '').trim();
    if (note) {
        return note;
    }
    return c.state ? `keine QM-Kraft für ${stateShort(c.state)} zugeordnet` : 'kein Bundesland bekannt';
};

export type Hint = { text: string; tone: Tone };

/** Small tags below the meta line ("warum") */
export const caseHints = (c: QualityCaseLean, maxAttempts?: number): Hint[] => {
    const hints: Hint[] = [];
    // closed cases: no live hints like "1 von 3 Versuche nicht erreicht" (the outcome tag says what happened)
    const attempts = c.status === QualityCaseStatus.OPEN ? (c.attempts || []).filter((a) => a.result === 'notReached').length : 0;
    if (attempts) {
        hints.push({text: `${attempts}${maxAttempts ? ` von ${maxAttempts}` : ''} ${attempts === 1 ? 'Versuch' : 'Versuche'} nicht erreicht`, tone: 'warn'});
    }
    if (c.type === QualityCaseType.RESCUE && c.terminationSnapshot?.monthlyNet) {
        hints.push({text: `${formatMoney(c.terminationSnapshot.monthlyNet)} / Monat`, tone: 'muted'});
    }
    if (c.type === QualityCaseType.GROUP && c.relatedTerminations?.length) {
        hints.push({text: `${c.relatedTerminations.length} ${c.relatedTerminations.length === 1 ? 'Kündigung' : 'Kündigungen'}`, tone: 'warn'});
    }
    if (c.type === QualityCaseType.ONBOARDING && c.prefill) {
        const absent = (c.prefill.attendance || []).filter((a) => a.state !== 0).length;
        const total = (c.prefill.attendance || []).length;
        if (total) {
            hints.push({text: `${total - absent} von ${total} Stunden da`, tone: absent ? 'warn' : 'good'});
        }
        if (c.prefill.timeChanges) {
            hints.push({text: `Uhrzeit ${c.prefill.timeChanges}× geändert`, tone: 'muted'});
        }
        if (c.prefill.bookShipped !== undefined) {
            // books go by post without tracking: only "verschickt"
            hints.push({text: c.prefill.bookShipped ? 'Buch verschickt' : 'Buch noch nicht verschickt', tone: c.prefill.bookShipped ? 'good' : 'warn'});
        }
    }
    if (c.type === QualityCaseType.RISK && c.risk) {
        c.risk.reasons.slice(0, 4).forEach((r) => hints.push({text: r.text, tone: 'warn'}));
    }
    if (c.type === QualityCaseType.COMPLAINT && c.complaint?.description) {
        hints.push({text: c.complaint.description.length > 80 ? `${c.complaint.description.slice(0, 80)} …` : c.complaint.description, tone: 'muted'});
    }
    if (c.type === QualityCaseType.RESCUE && c.display?.schoolChange) {
        hints.push({text: 'Schulwechsel: online / anderer Standort anbieten', tone: 'teal'});
    }
    if (c.terminationSnapshot?.description) {
        hints.push({text: `Notiz: ${c.terminationSnapshot.description}`, tone: 'muted'});
    }
    return hints;
};

export const formatMoney = (value: number): string =>
    value.toLocaleString('de-DE', {style: 'currency', currency: 'EUR', maximumFractionDigits: 2});

/** Due tag: "endet 31.10." for rescue, "heute fällig" / "3 Tage über" / "fällig 12.10." otherwise */
export const dueMeta = (c: QualityCaseLean, today = moment().startOf('day')): Hint | null => {
    if (!c.dueDate || c.status !== QualityCaseStatus.OPEN) {
        return null;
    }
    const due = moment(c.dueDate).startOf('day');
    const days = due.diff(today, 'days');
    if (c.type === QualityCaseType.RESCUE) {
        return {text: `endet ${fmtShort(c.dueDate)}`, tone: days <= 14 ? 'bad' : 'warn'};
    }
    if (c.type === QualityCaseType.COMPLAINT) {
        return {text: days < 0 ? `Frist ${-days} ${days === -1 ? 'Tag' : 'Tage'} über` : days === 0 ? 'Frist heute' : `Frist ${fmtShort(c.dueDate)}`, tone: days <= 0 ? 'bad' : 'warn'};
    }
    if (days < 0) {
        return {text: `${-days} ${days === -1 ? 'Tag' : 'Tage'} über`, tone: 'bad'};
    }
    if (days === 0) {
        return {text: 'heute fällig', tone: 'warn'};
    }
    return {text: `fällig ${fmtShort(c.dueDate)}`, tone: 'muted'};
};

/** Anruf-Fenster: the next attempt is planned later („Später“ – not in „Jetzt anrufen“ until then) */
export const isPlannedLater = (c: Pick<QualityCaseLean, 'nextAttemptAt'>, now = new Date()): boolean =>
    !!c.nextAttemptAt && new Date(c.nextAttemptAt).getTime() > now.getTime();

/** „Wieder dran ab Di 08:00“ for a case with a planned next attempt */
export const nextAttemptMeta = (c: Pick<QualityCaseLean, 'nextAttemptAt'>, now = new Date()): Hint | null => {
    if (!isPlannedLater(c, now)) {
        return null;
    }
    const at = moment(c.nextAttemptAt);
    const day = at.isSame(moment(now), 'day') ? 'heute' : at.isSame(moment(now).add(1, 'day'), 'day') ? 'morgen' : at.format('dd DD.MM.');
    return {text: `wieder dran ${day} ${at.format('HH:mm')}`, tone: 'muted'};
};

/** Due today or earlier (or no due date); a planned next attempt later on is not due */
export const isDue = (c: QualityCaseLean, today = moment().endOf('day')): boolean =>
    !isPlannedLater(c) && (c.type === QualityCaseType.RESCUE || !c.dueDate || moment(c.dueDate).isSameOrBefore(today));

export const isOverdue = (c: QualityCaseLean, today = moment().startOf('day')): boolean =>
    c.type !== QualityCaseType.RESCUE && !!c.dueDate && moment(c.dueDate).isBefore(today);

/** One row of the inbox: a case or an early warning record (read-only) */
export type InboxItem =
    | { kind: 'case'; key: string; rank: number; due: number; later?: boolean; item: QualityCaseLean }
    | { kind: 'ew'; key: string; rank: number; due: number; later?: boolean; item: AbsenceEarlyWarningRecordItem };

const FAR = Number.MAX_SAFE_INTEGER;

export const caseInboxItem = (c: QualityCaseLean): InboxItem => ({
    kind: 'case',
    key: String(c._id),
    rank: PRIORITY_META[c.priority]?.rank ?? 1,
    due: c.dueDate ? new Date(c.dueDate).getTime() : FAR,
    later: isPlannedLater(c),
    item: c,
});

/** Early warnings count as priority "normal", due at the trigger date */
export const ewInboxItem = (r: AbsenceEarlyWarningRecordItem): InboxItem => ({
    kind: 'ew',
    key: `ew-${r._id}`,
    rank: PRIORITY_META[QualityCasePriority.NORMAL].rank,
    due: r.triggeredAt ? new Date(r.triggeredAt).getTime() : FAR,
    item: r,
});

/** Priority (high → low), then due date (earliest first) */
/** Priority, then due date; cases with a later planned attempt („Später“) come last */
export const sortInbox = (items: InboxItem[]): InboxItem[] => items.slice().sort((a, b) =>
    Number(!!a.later) - Number(!!b.later) || a.rank - b.rank || a.due - b.due);

/** Onboarding: due date of a touchpoint that is not created yet (null for "after the n-th lesson") */
export const plannedTouchpointDate = (kind: QualityOnboardingTouchpointKind, value: number, start?: Date | string): Date | null => {
    if (!start) {
        return null;
    }
    if (kind === QualityOnboardingTouchpointKind.DAYS_AFTER_START) {
        return moment(start).add(value, 'days').toDate();
    }
    if (kind === QualityOnboardingTouchpointKind.WEEKS_AFTER_START) {
        return moment(start).add(value, 'weeks').toDate();
    }
    return null;
};

export const TOUCHPOINT_KIND_TEXT: Record<QualityOnboardingTouchpointKind, { label: string; unit: string }> = {
    [QualityOnboardingTouchpointKind.DAYS_AFTER_START]: {label: 'Tage nach Kursbeginn des Kindes', unit: 'Tage'},
    [QualityOnboardingTouchpointKind.AFTER_NTH_LESSON]: {label: '. gehaltene Stunde', unit: 'Stunde'},
    [QualityOnboardingTouchpointKind.WEEKS_AFTER_START]: {label: 'Wochen nach Kursbeginn des Kindes', unit: 'Wochen'},
    [QualityOnboardingTouchpointKind.TEACHING_WEEKS_AFTER_START]: {label: '. Unterrichtswoche (ohne Ferienwochen)', unit: 'Unterrichtswoche'},
};

/** Emoji of a feedback rating / answer outcome (good / neutral / bad) */
export const FEEDBACK_EMOJI: Record<string, string> = {
    [SimpleRating.GOOD]: '😊', [SimpleRating.NEUTRAL]: '😐', [SimpleRating.BAD]: '☹️', good: '😊', neutral: '😐', bad: '☹️',
};

export const FEEDBACK_TONE: Record<string, Tone> = {
    [SimpleRating.GOOD]: 'good', [SimpleRating.NEUTRAL]: 'warn', [SimpleRating.BAD]: 'bad', good: 'good', neutral: 'warn', bad: 'bad',
};

/** Percent with one decimal, German ("5,9 %"); "–" for null */
export const pct = (value: number | null | undefined, digits = 1): string => value === null || value === undefined || !Number.isFinite(value)
    ? '–' : `${(value * 100).toLocaleString('de-DE', {minimumFractionDigits: digits, maximumFractionDigits: digits})} %`;

export const num = (value: number | null | undefined, digits = 0): string => value === null || value === undefined || !Number.isFinite(value)
    ? '–' : value.toLocaleString('de-DE', {maximumFractionDigits: digits});

/** Months of a period (at least 1/30), for "je Monat" figures */
export const monthsOf = (from: Date | string, to: Date | string): number =>
    Math.max(1 / 30, moment(to).diff(moment(from), 'days') / 30.4375);

/** Every status except open (done, closedAuto and newer ones such as expired / discarded) */
export const closedStatuses = (): QualityCaseStatus[] => (Object.values(QualityCaseStatus) as unknown[])
    .filter((value): value is QualityCaseStatus => typeof value === 'string' && value !== QualityCaseStatus.OPEN);

/** "Bleibt" / "nicht bearbeitet" / "verworfen" of a closed case */
export const closedLabel = (c: QualityCaseLean): string => {
    const outcome = c.outcome?.outcome;
    if (c.status === QualityCaseStatus.DONE && outcome) {
        return outcomeText(c.type, outcome);
    }
    if (c.status === QualityCaseStatus.DISCARDED) {
        return 'verworfen';
    }
    if (c.status === QualityCaseStatus.EXPIRED || c.status === QualityCaseStatus.CLOSED_AUTO) {
        return `nicht bearbeitet${CLOSED_REASON_TEXT[c.closedReason || ''] ? ` (${CLOSED_REASON_TEXT[c.closedReason || '']})` : ''}`;
    }
    return outcome ? outcomeText(c.type, outcome) : 'geschlossen';
};

// ---------------------------------------------------------------- QM-Fallakte: grouped inbox, contacts

/** Several contacts per case (parents of every child + teacher; Ansprechpartner): attempts per contact, no case-level "nicht erreicht" */
export const isMultiContact = (c: Pick<QualityCaseLean, 'type'>): boolean => QUALITY_MULTI_CONTACT_TYPES.includes(c.type);

export type NoteTargetKind = 'student' | 'institution' | 'teacher' | 'none';

/**
 * Whose protocol the outcome note (Fazit) lands in – mirror of the backend (QualityCasesService.protocolTarget + the talk
 * outcomes of teacher / Standort cases); the dossier's noteTargetKind wins when loaded. Complaint about a child → the
 * child, about a teacher only → the teacher, else the Standort.
 */
export function noteTargetKindOf(c: Pick<QualityCaseLean, 'type' | 'student' | 'teacher' | 'institution' | 'checkInTarget' | 'onboardingTarget'>): NoteTargetKind {
    if (c.type === QualityCaseType.ONBOARDING && c.onboardingTarget && c.onboardingTarget !== QualityOnboardingTarget.CHILD) {
        return c.onboardingTarget === QualityOnboardingTarget.TEACHER ? 'teacher' : 'institution';
    }
    if (c.type === QualityCaseType.TEACHER || (c.type === QualityCaseType.CHECK_IN && c.checkInTarget === QualityCheckInTarget.TEACHER)) {
        return 'teacher';
    }
    if (c.type === QualityCaseType.INSTITUTION || c.type === QualityCaseType.CHECK_IN) {
        return 'institution';
    }
    if (c.type === QualityCaseType.COMPLAINT && !c.student && c.teacher) {
        return 'teacher';
    }
    if (c.student && c.type !== QualityCaseType.GROUP) {
        return 'student';
    }
    return c.institution ? 'institution' : 'none';
}

/** "landet im Protokoll von Mia K." – fallback text when the dossier (noteTarget) is not loaded */
export function noteTargetTextOf(c: Pick<QualityCaseLean, 'type' | 'student' | 'teacher' | 'institution' | 'checkInTarget' | 'onboardingTarget' | 'display'
    | 'courseCustomId'>): string {
    const kind = noteTargetKindOf(c);
    const d = c.display || {};
    if (kind === 'none') {
        return 'wird nur im Fall gespeichert';
    }
    if (kind === 'teacher') {
        return c.type === QualityCaseType.COMPLAINT ? `landet im Protokoll von ${d.teacherName || 'der Lehrkraft'}`
            : `landet als QM-Gespräch im Profil von ${d.teacherName || 'der Lehrkraft'}`;
    }
    if (kind === 'institution') {
        const course = c.type === QualityCaseType.GROUP && c.courseCustomId ? ` (Kurs ${c.courseCustomId})` : '';
        return `landet im Protokoll des Standorts${d.institutionName ? ` ${d.institutionName}` : ''}${course}`;
    }
    return `landet im Protokoll von ${d.studentName || 'dem Kind'}`;
}

/** Onboarding case of a new child (rating 1–5, "Alles gut" / "Problem") */
export const isChildOnboarding = (c: Pick<QualityCaseLean, 'type' | 'onboardingTarget'>): boolean =>
    c.type === QualityCaseType.ONBOARDING && onboardingTargetOf(c) === QualityOnboardingTarget.CHILD;

/** Talk with a teacher (teacher rating dimensions): teacher case, teacher check-in, onboarding of a new teacher */
export const isTeacherTalkCase = (c: Pick<QualityCaseLean, 'type' | 'checkInTarget' | 'onboardingTarget'>): boolean =>
    c.type === QualityCaseType.TEACHER || (c.type === QualityCaseType.CHECK_IN && c.checkInTarget === QualityCheckInTarget.TEACHER)
    || (c.type === QualityCaseType.ONBOARDING && onboardingTargetOf(c) === QualityOnboardingTarget.TEACHER);

/** Outcomes offered for a case (onboarding per target group: teacher / Standort record a talk) */
export const outcomesOf = (c: Pick<QualityCaseLean, 'type' | 'onboardingTarget'> & { trigger?: string }): QualityCaseOutcome[] => {
    if (c.type === QualityCaseType.GROUP) {
        // Kleingruppe (Spec Verbleib Teil 6): its four outcomes in the order of the Akte; other group cases the classic ones
        const small = QualityCaseOutcome.SMALL_GROUP || [];
        return c.trigger === 'smallGroup' ? small : (QualityCaseOutcome.ByType[c.type] || []).filter((o) => !small.includes(o));
    }
    return (c.type === QualityCaseType.ONBOARDING ? QualityCaseOutcome.ByOnboardingTarget[onboardingTargetOf(c)] : QualityCaseOutcome.ByType[c.type]) || [];
};

/** Placeholder of the Fazit / note field per case type */
export function notePlaceholderOf(type: QualityCaseType, kind: NoteTargetKind): string {
    switch (type) {
        case QualityCaseType.GROUP:
            return 'z. B. Gruppe bleibt, Werbung am Standort geplant …';
        case QualityCaseType.COMPLAINT:
            return kind === 'student' ? 'z. B. mit der Familie gesprochen, Lösung vereinbart und zurückgemeldet …'
                : kind === 'teacher' ? 'z. B. mit der Lehrkraft besprochen, Hospitation vereinbart …'
                    : 'z. B. mit der Schule gesprochen, Lösung vereinbart …';
        case QualityCaseType.INSTITUTION:
            return 'z. B. Schule zufrieden, neue Klassen ab Februar …';
        case QualityCaseType.CHECK_IN:
            return kind === 'teacher' ? 'z. B. Unterricht läuft gut, nächstes Gespräch im Frühjahr …' : 'z. B. Schule zufrieden, neue Klassen ab Februar …';
        case QualityCaseType.TEACHER:
            return 'z. B. Gründe besprochen, Unterstützung vereinbart …';
        case QualityCaseType.RESCUE:
            return 'z. B. Grund der Kündigung, was angeboten wurde …';
        case QualityCaseType.ONBOARDING:
            return kind === 'teacher' ? 'z. B. gut gestartet, braucht noch Material …' : kind === 'institution' ? 'z. B. Schule zufrieden, Raum passt …'
                : 'z. B. Kind ist gut angekommen, Familie zufrieden …';
        case QualityCaseType.RISK:
            return 'z. B. Fehlzeiten wegen Krankheit, Familie zufrieden …';
        case QualityCaseType.TRANSITION:
            return 'z. B. wechselt aufs Gymnasium, macht online weiter …';
        default:
            return 'Notiz zum Gespräch …';
    }
}

/** "3 von 5 Kontakten erreicht" (contacts that left are not counted); '' without contacts */
export const contactProgress = (c: Pick<QualityCaseLean, 'contacts'>): { reached: number; open: number; total: number; text: string } => {
    // optional contacts (teachers of a Standort case) are not counted
    const list = (c.contacts || []).filter((x) => !x.left && !x.optional);
    const reached = list.filter((x) => x.status === QualityCaseContactStatus.REACHED).length;
    const open = list.filter((x) => x.status === QualityCaseContactStatus.OPEN).length;
    return {reached, open, total: list.length, text: list.length ? `${reached} von ${list.length} ${list.length === 1 ? 'Kontakt' : 'Kontakten'} erreicht` : ''};
};

/** Grouped inbox: sub-cases hang below their parent if the parent is in the list; every case is counted once */
export function groupCases<T extends { _id: unknown; parentCase?: unknown }>(cases: T[]): Array<{ item: T; children: T[] }> {
    const ids = new Set(cases.map((c) => String(c._id)));
    const children = new Map<string, T[]>();
    const top: T[] = [];
    for (const c of cases) {
        const parent = c.parentCase ? String(c.parentCase) : '';
        if (parent && parent !== String(c._id) && ids.has(parent)) {
            children.set(parent, [...(children.get(parent) || []), c]);
        } else {
            top.push(c);
        }
    }
    return top.map((item) => ({item, children: children.get(String(item._id)) || []}));
}

/** Short relation text of a sub-case ("gehört zum Standortfall", "Kind von Jonas W.") */
export const subCaseText = (sub: QualityCaseLean, parent: QualityCaseLean): string => {
    if (parent.type === QualityCaseType.INSTITUTION) {
        return 'gehört zum Standortfall · eigene Erledigung möglich';
    }
    if (sub.type === QualityCaseType.RESCUE) {
        return `Kind von ${parent.display?.teacherName || 'der Lehrkraft'}`;
    }
    return `Kurs von ${parent.display?.teacherName || 'der Lehrkraft'}`;
};

/** Open case includes the interval talk of a teacher / Standort (chip "inkl. Regelgespräch") */
export const includesOpenCheckIn = (c: Pick<QualityCaseLean, 'includesCheckIn'>): boolean => (c.includesCheckIn || []).some((x) => !x.doneAt);

// ---------------------------------------------------------------- Paket 2

/** Score tone of the Risiko-Liste (higher = worse) */
export const riskTone = (score: number): Tone => (score >= 60 ? 'bad' : score >= 40 ? 'warn' : 'muted');

/** Health tone of the Standort-Gesundheit (lower = worse) */
export const healthTone = (score: number, low = 50): Tone => (score < low ? 'bad' : score < 75 ? 'warn' : 'good');

export const SCORECARD_FLAG_TEXT: Record<string, string> = {
    terminationRate: 'Kündigungsquote hoch',
    cancellationRate: 'viele Ausfälle',
    missingLogs: 'Protokolle fehlen',
    talkTrend: 'QM-Bewertung gesunken',
};

// ---------------------------------------------------------------- QM-Fallakte: result banner

export type CaseBanner = { tone: Tone; title: string; sub: string; icon: string };

/**
 * The story of a case at a glance (top of the Akte): open rescue "Kündigung zum 30.11.2026 · noch 52 Tage", closed
 * rescue "Kündigung zum 30.11.2026 zurückgewonnen – pausiert bis 20.01.2027" / "Kündigung bleibt – Kind geht zum …",
 * other types: what happened + result; discarded / not processed grey. `names`: login user id → name (decided by).
 */
export function caseBanner(c: QualityCaseLean, names: Record<string, string> = {}, now: Date = new Date()): CaseBanner {
    const due = c.terminationSnapshot?.dueDate || (c.type === QualityCaseType.RESCUE ? c.dueDate : undefined);
    const dueText = due ? fmtDate(due) : '';
    const outcome = c.outcome?.outcome;
    const params = (c.outcome?.params || {}) as Record<string, any>;
    const decidedAt = c.outcome?.decidedAt || c.closedAt;
    const by = c.outcome?.decidedBy ? names[String(c.outcome.decidedBy)] : '';
    const decided = decidedAt ? `entschieden am ${fmtDate(decidedAt)}${by ? ` von ${by}` : ''}` : '';
    if (c.status === QualityCaseStatus.OPEN) {
        if (c.type === QualityCaseType.RESCUE && due) {
            const days = moment(due).startOf('day').diff(moment(now).startOf('day'), 'days');
            return {
                tone: days <= 14 ? 'bad' : 'warn', icon: 'mdi-timer-sand',
                title: days >= 0 ? `Kündigung zum ${dueText} · noch ${days} ${days === 1 ? 'Tag' : 'Tage'}` : `Kündigung zum ${dueText} · seit ${-days} Tagen wirksam`,
                sub: c.dueDate && c.dueDate !== due ? `Fall fällig ${fmtDate(c.dueDate)}` : 'Rückgewinnung offen',
            };
        }
        return {tone: 'info', icon: 'mdi-progress-clock', title: `${caseTitle(c).split(' · ')[0]} – offen`, sub: c.dueDate ? `fällig ${fmtDate(c.dueDate)}` : ''};
    }
    if (c.status === QualityCaseStatus.DISCARDED) {
        return {tone: 'muted', icon: 'mdi-close-circle-outline', title: 'Verworfen', sub: [c.outcome?.note, decided].filter(Boolean).join(' · ')};
    }
    if (c.status === QualityCaseStatus.EXPIRED || c.status === QualityCaseStatus.CLOSED_AUTO) {
        return {tone: 'muted', icon: 'mdi-minus-circle-outline', title: closedLabel(c).replace(/^n/, 'N'), sub: decidedAt ? `geschlossen am ${fmtDate(decidedAt)}` : ''};
    }
    if (c.type === QualityCaseType.RESCUE) {
        const won = `Kündigung${dueText ? ` zum ${dueText}` : ''} zurückgewonnen`;
        switch (outcome) {
            case QualityCaseOutcome.STAYS:
                return {tone: 'good', icon: 'mdi-check-circle-outline', title: `${won} – bleibt`, sub: decided};
            case QualityCaseOutcome.OTHER_COURSE:
                return {tone: 'good', icon: 'mdi-check-circle-outline', sub: decided,
                    title: `${won} – in anderen Kurs${params.targetCustomId ? ` ${params.targetCustomId}` : ''}${params.switchDate ? ` ab ${fmtShort(params.switchDate)}` : ''}`};
            case QualityCaseOutcome.ONLINE:
                return {tone: 'good', icon: 'mdi-check-circle-outline', sub: decided,
                    title: `${won} – wechselt auf online${params.targetCustomId ? ` (${params.targetCustomId})` : ''}${params.switchDate ? ` ab ${fmtShort(params.switchDate)}` : ''}`};
            case QualityCaseOutcome.PAUSED:
                return {tone: 'good', icon: 'mdi-check-circle-outline', sub: decided, title: `${won} – pausiert${params.pauseUntil ? ` bis ${fmtDate(params.pauseUntil)}` : ''}`};
            case QualityCaseOutcome.LEAVES:
                return {tone: 'bad', icon: 'mdi-account-arrow-right-outline', sub: decided, title: `Kündigung bleibt – Kind geht${dueText ? ` zum ${dueText}` : ''}`};
            case QualityCaseOutcome.NOT_REACHED:
                return {tone: 'bad', icon: 'mdi-phone-missed-outline', sub: decided, title: `Nicht erreicht – Kündigung bleibt${dueText ? ` zum ${dueText}` : ''}`};
            default:
                break;
        }
    }
    const tone = outcomeTone(outcome);
    return {
        tone, icon: tone === 'good' ? 'mdi-check-circle-outline' : tone === 'bad' ? 'mdi-alert-circle-outline' : 'mdi-information-outline',
        title: `${caseTitle(c).split(' · ')[0]}: ${closedLabel(c)}`, sub: [decided, c.outcome?.note ? `„${c.outcome.note}“` : ''].filter(Boolean).join(' · '),
    };
}
